import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  where,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage, auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import emailjs from "emailjs-com";
import "./career.css";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext"; // ✅ Import AuthContext

import careerPageBg from './image/career1.png';
import MbcareerPageBg from './image/prrepx_career1.png';

import SocialSidebar from "../components/SocialSideBar";

export default function Career() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth(); // ✅ Use centralized AuthContext

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    resume: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);

  // Career positions admin access
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasCareerAccess, setHasCareerAccess] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageTab, setManageTab] = useState('positions');
  const [editingPosition, setEditingPosition] = useState(null);
  const [positionForm, setPositionForm] = useState({ title: '', description: '', location: '', responsibilities: '', requirements: '' });
  const [savingPosition, setSavingPosition] = useState(false);
  const [deletingPositionId, setDeletingPositionId] = useState(null);
  const [privilegedUsers, setPrivilegedUsers] = useState([]);
  const [grantEmail, setGrantEmail] = useState('');
  const [granting, setGranting] = useState(false);

  const testimonials = [
    {
      name: "Hanumat Lal Vishwakarma",
      role: "Founder & Managing Director",
      quote:
        "At PrrepX Pathshala, we’re not just teaching—we’re rewriting the rules of education with unmatched quality and vision.",
    },
    {
      name: "Neelam Vishvakarma",
      role: "Co-founder & Technical Management",
      quote:
        "We fuse technology with innovation to create seamless, world-class learning experiences that inspire and empower.",
    },
    {
      name: "Lavkush Kumar Singh",
      role: "Co-founder & Director",
      quote:
        "This is India’s new revolution in learning—modern, bold, and driven to unlock every student’s highest potential.",
    },
    {
      name: "Raju Vishwas",
      role: "Co-founder & Director",
      quote:
        "Mathematics becomes a language of confidence at PrrepX—solving challenges with clarity, creativity, and fire.",
    },
    {
      name: "Harpal Singh",
      role: "Co-founder & Business Head",
      quote:
        "Building one stop solutions for every aspirants—it’s curiosity, discovery, and preparing young minds to lead the future.",
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(testimonials.length);
  const [disableTransition, setDisableTransition] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef();
  const isPausedRef = useRef(false);
  const totalTestimonials = testimonials.length;

  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  // Auto-play: pause when hovered, run every 7 s
  useEffect(() => {
    if (isPaused) {
      clearInterval(intervalRef.current);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(
        () => setCurrentTestimonial((prev) => prev + 1),
        7000
      );
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused]);

  // True-infinite: triple-clone boundary reset (instant jump, no visible snap)
  useEffect(() => {
    if (currentTestimonial >= totalTestimonials * 2) {
      const timer = setTimeout(() => {
        setDisableTransition(true);
        setCurrentTestimonial(totalTestimonials);
        setTimeout(() => setDisableTransition(false), 50);
      }, 1050);
      return () => clearTimeout(timer);
    }
    if (currentTestimonial < 0) {
      const timer = setTimeout(() => {
        setDisableTransition(true);
        setCurrentTestimonial(totalTestimonials * 2 - 1);
        setTimeout(() => setDisableTransition(false), 50);
      }, 1050);
      return () => clearTimeout(timer);
    }
  }, [currentTestimonial, totalTestimonials]);

  const goNext = () => {
    setCurrentTestimonial((prev) => prev + 1);
    if (!isPausedRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(
        () => setCurrentTestimonial((prev) => prev + 1),
        7000
      );
    }
  };

  const goPrev = () => {
    setCurrentTestimonial((prev) => prev - 1);
    if (!isPausedRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(
        () => setCurrentTestimonial((prev) => prev + 1),
        7000
      );
    }
  };

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const q = query(
          collection(db, "openPositions"),
          orderBy("openedAt", "desc")
        );
        const querySnapshot = await getDocs(q);
        const pos = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPositions(pos);
        setLoadingPositions(false);
      } catch (error) {
        console.error("Error fetching positions: ", error);
        setLoadingPositions(false);
      }
    };
    fetchPositions();
  }, []);

  // Check if current user has career management access
  useEffect(() => {
    if (!currentUser) {
      setIsSuperAdmin(false);
      setHasCareerAccess(false);
      return;
    }
    if (userData?.isSuperAdmin === true) {
      setIsSuperAdmin(true);
      setHasCareerAccess(true);
      return;
    }
    const accessRef = doc(db, 'careerManageAccess', currentUser.uid);
    const unsubscribe = onSnapshot(accessRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().hasAccess === true) {
        setHasCareerAccess(true);
      } else {
        setHasCareerAccess(false);
      }
    });
    return unsubscribe;
  }, [currentUser, userData]);

  // Load list of privileged users (superadmin only, for access management UI)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const privilegedRef = collection(db, 'careerManageAccess');
    const unsubscribe = onSnapshot(privilegedRef, (snapshot) => {
      const data = snapshot.docs
        .filter(d => d.data().hasAccess === true)
        .map(d => ({ userId: d.id, ...d.data() }));
      setPrivilegedUsers(data);
    });
    return unsubscribe;
  }, [isSuperAdmin]);

  const handleChange = (e) => {
    if (e.target.name === "resume")
      setFormData({ ...formData, resume: e.target.files[0] });
    else setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (!formData.resume) return toast.error("Please upload your resume!");
    setSubmitting(true);

    try {
      const fileName = formData.resume.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const storageRef = ref(storage, `resumes/${fileName}-${Date.now()}`);
      const snapshot = await uploadBytesResumable(storageRef, formData.resume);
      const resumeUrl = await getDownloadURL(snapshot.ref);

      const firestoreData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        resumeUrl,
        submittedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "applications"), firestoreData);

      await emailjs.send(
        "wenvhlv",
        "template_h6t8i65",
        {
          to_name: formData.name,
          to_email: formData.email,
          role: formData.role,
        },
        "nwPm1S8U2W5i1NRsn"
      );

      setFormData({ name: "", email: "", phone: "", role: "", resume: null });
      toast.success(
        "Application submitted successfully! You will soon get a confirmation mail."
      );
      setShowApplyModal(false);
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong! " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyClick = (title) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    setFormData({ ...formData, role: title });
    setShowApplyModal(true);
  };

  // ── Career Manage: Grant access by email ──────────────────────────────────
  const handleGrantAccess = async () => {
    if (!grantEmail.trim()) return toast.error('Please enter an email');
    setGranting(true);
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', grantEmail.trim().toLowerCase())
      );
      const usersSnap = await getDocs(usersQuery);
      if (usersSnap.empty) {
        toast.error('No user found with this email');
        setGranting(false);
        return;
      }
      const userDoc = usersSnap.docs[0];
      if (userDoc.data().isSuperAdmin) {
        toast.info('SuperAdmins already have full access');
        setGranting(false);
        return;
      }
      const alreadyGranted = privilegedUsers.some(p => p.userId === userDoc.id);
      if (alreadyGranted) {
        toast.info('This user already has access');
        setGranting(false);
        return;
      }
      await setDoc(doc(db, 'careerManageAccess', userDoc.id), {
        hasAccess: true,
        grantedAt: serverTimestamp(),
        grantedBy: currentUser.uid,
        grantedByEmail: currentUser.email,
        userEmail: userDoc.data().email,
        userName: userDoc.data().username || userDoc.data().email,
      });
      toast.success(`Access granted to ${userDoc.data().username || userDoc.data().email}`);
      setGrantEmail('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  // ── Career Manage: Revoke access ─────────────────────────────────────────
  const handleRevokeAccess = async (userId, userName) => {
    if (!window.confirm(`Revoke career management access for ${userName}?`)) return;
    try {
      await deleteDoc(doc(db, 'careerManageAccess', userId));
      toast.success(`Access revoked for ${userName}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to revoke access');
    }
  };

  // ── Career Manage: Add new position ──────────────────────────────────────
  const handleAddPosition = async (e) => {
    e.preventDefault();
    if (!positionForm.title.trim()) return toast.error('Title is required');
    setSavingPosition(true);
    try {
      const responsibilities = positionForm.responsibilities
        .split('\n').map(s => s.trim()).filter(Boolean);
      const requirements = positionForm.requirements
        .split('\n').map(s => s.trim()).filter(Boolean);
      await addDoc(collection(db, 'openPositions'), {
        title: positionForm.title.trim(),
        description: positionForm.description.trim(),
        location: positionForm.location.trim(),
        responsibilities,
        requirements,
        openedAt: serverTimestamp(),
        addedBy: currentUser.uid,
      });
      const q = query(collection(db, 'openPositions'), orderBy('openedAt', 'desc'));
      const snap = await getDocs(q);
      setPositions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPositionForm({ title: '', description: '', location: '', responsibilities: '', requirements: '' });
      toast.success('Position added successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add position');
    } finally {
      setSavingPosition(false);
    }
  };

  // ── Career Manage: Update existing position ───────────────────────────────
  const handleUpdatePosition = async (e) => {
    e.preventDefault();
    if (!editingPosition) return;
    setSavingPosition(true);
    try {
      const responsibilities = positionForm.responsibilities
        .split('\n').map(s => s.trim()).filter(Boolean);
      const requirements = positionForm.requirements
        .split('\n').map(s => s.trim()).filter(Boolean);
      await updateDoc(doc(db, 'openPositions', editingPosition.id), {
        title: positionForm.title.trim(),
        description: positionForm.description.trim(),
        location: positionForm.location.trim(),
        responsibilities,
        requirements,
      });
      const q = query(collection(db, 'openPositions'), orderBy('openedAt', 'desc'));
      const snap = await getDocs(q);
      setPositions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setEditingPosition(null);
      setPositionForm({ title: '', description: '', location: '', responsibilities: '', requirements: '' });
      toast.success('Position updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update position');
    } finally {
      setSavingPosition(false);
    }
  };

  // ── Career Manage: Delete position ───────────────────────────────────────
  const handleDeletePosition = async (posId, posTitle) => {
    if (!window.confirm(`Delete position "${posTitle}"?`)) return;
    setDeletingPositionId(posId);
    try {
      await deleteDoc(doc(db, 'openPositions', posId));
      setPositions(prev => prev.filter(p => p.id !== posId));
      toast.success('Position deleted');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete position');
    } finally {
      setDeletingPositionId(null);
    }
  };

  // ── Career Manage: Open edit form for a position ──────────────────────────
  const startEditPosition = (pos) => {
    setEditingPosition(pos);
    setPositionForm({
      title: pos.title || '',
      description: pos.description || '',
      location: pos.location || '',
      responsibilities: Array.isArray(pos.responsibilities)
        ? pos.responsibilities.join('\n')
        : Object.values(pos.responsibilities || {}).join('\n'),
      requirements: Array.isArray(pos.requirements)
        ? pos.requirements.join('\n')
        : Object.values(pos.requirements || {}).join('\n'),
    });
    setManageTab('positions');
    setShowManageModal(true);
  };

  const openManageModal = () => {
    setEditingPosition(null);
    setPositionForm({ title: '', description: '', location: '', responsibilities: '', requirements: '' });
    setManageTab('positions');
    setShowManageModal(true);
  };

  const handleHeaderCTA = () => {
    document
      .getElementById("open-positions")
      .scrollIntoView({ behavior: "smooth" });
  };

  const visiblePositions = showAll ? positions : positions.slice(0, 4);

  return (
<div className="career-page">
      
      <SocialSidebar />
      {/* Hero Section */}
      <div className="cr-heroSection">
        <img src={careerPageBg} alt="" className="cr-heroDesktopBg" />
        <div className="cr-heroGlow cr-glow1" />
        <div className="cr-heroGlow cr-glow2" />
        <div className="cr-heroGlow cr-glow3" />

        <div className="cr-heroMobileImg">
          <img src={MbcareerPageBg} alt="Career at PrrepX" className="cr-heroMobileSlideImg" />
          <div className="cr-heroMobileImgBadge">🚀 Join a pool of 100% IIT/NIT & top Medical Alumni</div>
        </div>

        <div className="cr-heroContent">
          <div className="cr-heroBadgePill">🚀 Join a pool of 100% IIT/NIT & top Medical Alumni</div>
          <h1 className="cr-heroHeading">
            Build Your Career at <span className="cr-heroAccent">PrrepX Pathshala</span>
          </h1>
          <p className="cr-heroTagline">
            Join India's leading EdTech platform. Competitive pay, career growth, and inspiring culture.
          </p>
          <div className="cr-heroButtons">
            <button className="cr-btnPrimary" onClick={handleHeaderCTA}>View Open Positions</button>
          </div>
        </div>
      </div>
      <motion.section 
        className="life-at-prepx-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="section-title">Life at PrrepX Pathshala</h2>
        <div className="life-cards-grid">
          <div className="life-card gradient-blue">
            <h3>💰 Competitive Salary & Benefits</h3>
            <p>
              Earn one of the most competitive salary packages in the market 
              along with performance bonuses and incentives for excellence.
            </p>
          </div>
          <div className="life-card gradient-yellow">
            <h3>📈 Career Growth & Leadership</h3>
            <p>
              Get opportunities to lead academic initiatives, design content, 
              and mentor junior faculty while growing into a thought leader.
            </p>
          </div>
          <div className="life-card gradient-green">
            <h3>⚖️ Work-Life Balance</h3>
            <p>
              We value your time — with structured schedules and a healthy work 
              environment that allows you to focus on teaching and personal well-being.
            </p>
          </div>
          <div className="life-card gradient-purple">
            <h3>🌟 Respect & Recognition</h3>
            <p>
              Be part of a culture that celebrates educators and gives them 
              the respect they truly deserve for shaping future generations.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        className="testimonials gradient-blue"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2>Voice of Our Team</h2>
        <div className="testimonial-carousel-nav">
          <button className="testimonial-nav-btn" onClick={goPrev} aria-label="Previous">&#10094;</button>
          <div
            className="testimonial-carousel-wrapper"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <motion.div
              className="testimonial-carousel"
              animate={{ x: `-${currentTestimonial * 100}%` }}
              transition={disableTransition ? { duration: 0 } : { type: "tween", duration: 1.2, ease: "easeInOut" }}
            >
              {[...testimonials, ...testimonials, ...testimonials].map((t, idx) => (
                <div key={idx} className="testimonial-card">
                  <p>"{t.quote}"</p>
                  <h4>{t.name}</h4>
                  <span>{t.role}</span>
                </div>
              ))}
            </motion.div>
          </div>
          <button className="testimonial-nav-btn" onClick={goNext} aria-label="Next">&#10095;</button>
        </div>
        <div className="testimonial-dots">
          {testimonials.map((_, idx) => (
            <span
              key={idx}
              className={
                idx === currentTestimonial % testimonials.length
                  ? "dot active-dot"
                  : "dot"
              }
              onClick={() => setCurrentTestimonial(testimonials.length + idx)}
            ></span>
          ))}
        </div>
      </motion.section>

      {/* Open Positions Section */}
      <motion.section
        className="open-positions"
        id="open-positions"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2>Open Positions</h2>
        {hasCareerAccess && (
          <div className="open-positions-header">
            <button className="career-manage-btn" onClick={openManageModal}>
              ⚙️ Manage Positions
            </button>
          </div>
        )}
        {loadingPositions ? (
          <p>Loading open positions...</p>
        ) : positions.length === 0 ? (
          <p>No positions available at the moment.</p>
        ) : (
          <div className="positions-grid">
            {visiblePositions.map((pos, index) => (
              <div key={pos.id} className="position-card">
                <h3>
                  {index + 1}. {pos.title}
                </h3>
                <p>{pos.description}</p>

                {/* Expandable */}
                <div
                  className={`expandable ${expandedIndex === index ? "open" : ""}`}
                >
                  <div className="expandable-content">
                    <h4>Responsibilities</h4>
                    <ul>
                      {Array.isArray(pos.responsibilities)
                        ? pos.responsibilities.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))
                        : Object.values(pos.responsibilities || {}).map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                    </ul>
                    <h4>Requirements</h4>
                    <ul>
                      {Array.isArray(pos.requirements)
                        ? pos.requirements.map((req, i) => <li key={i}>{req}</li>)
                        : Object.values(pos.requirements || {}).map((req, i) => (
                            <li key={i}>{req}</li>
                          ))}
                    </ul>
                    <p>
                      <strong>Location:</strong> {pos.location}
                    </p>
                  </div>
                </div>

                <button
                  className="toggle-btn"
                  onClick={() =>
                    setExpandedIndex(expandedIndex === index ? null : index)
                  }
                >
                  {expandedIndex === index ? "Show Less" : "More"}{" "}
                  <span className={`arrow ${expandedIndex === index ? "down" : ""}`}>
                    ▶
                  </span>
                </button>

                {pos.openedAt ? (
                  <small>
                    Opened on:{" "}
                    {new Date(pos.openedAt.seconds * 1000).toLocaleDateString()}
                  </small>
                ) : (
                  <small>Opened on: Not available</small>
                )}

                <button
                  className="apply-now-btn"
                  onClick={() => handleApplyClick(pos.title)}
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        )}
        {positions.length > 4 && (
          <button className="show-more-btn" onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show Less" : "Show More"}
          </button>
        )}
      </motion.section>

      {/* ── Career Management Modal (admin/superadmin only) ── */}
      {showManageModal && hasCareerAccess && (
        <div className="career-manage-overlay" onClick={() => setShowManageModal(false)}>
          <div className="career-manage-modal" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="cmm-header">
              <h3>⚙️ Manage Careers</h3>
              <button className="cmm-close" onClick={() => setShowManageModal(false)}>✕</button>
            </div>

            {/* Tabs */}
            <div className="cmm-tabs">
              <button
                className={`cmm-tab ${manageTab === 'positions' ? 'active' : ''}`}
                onClick={() => { setManageTab('positions'); setEditingPosition(null); setPositionForm({ title: '', description: '', location: '', responsibilities: '', requirements: '' }); }}
              >📋 Positions</button>
              {isSuperAdmin && (
                <button
                  className={`cmm-tab ${manageTab === 'access' ? 'active' : ''}`}
                  onClick={() => setManageTab('access')}
                >🔐 Access Control</button>
              )}
            </div>

            {/* ── Positions Tab ── */}
            {manageTab === 'positions' && (
              <div className="cmm-body">
                {/* Add / Edit Form */}
                <div className="cmm-form-section">
                  <h4>{editingPosition ? `✏️ Edit: ${editingPosition.title}` : '➕ Add New Position'}</h4>
                  <form onSubmit={editingPosition ? handleUpdatePosition : handleAddPosition} className="cmm-form">
                    <div className="cmm-form-row">
                      <div className="cmm-field">
                        <label>Job Title *</label>
                        <input
                          type="text"
                          placeholder="e.g. Physics Faculty"
                          value={positionForm.title}
                          onChange={e => setPositionForm(p => ({ ...p, title: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="cmm-field">
                        <label>Location</label>
                        <input
                          type="text"
                          placeholder="e.g. Remote / Delhi"
                          value={positionForm.location}
                          onChange={e => setPositionForm(p => ({ ...p, location: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="cmm-field">
                      <label>Description</label>
                      <textarea
                        rows={2}
                        placeholder="Short description of the role"
                        value={positionForm.description}
                        onChange={e => setPositionForm(p => ({ ...p, description: e.target.value }))}
                      />
                    </div>
                    <div className="cmm-form-row">
                      <div className="cmm-field">
                        <label>Responsibilities (one per line)</label>
                        <textarea
                          rows={4}
                          placeholder={"Deliver engaging lectures\nAssess student progress"}
                          value={positionForm.responsibilities}
                          onChange={e => setPositionForm(p => ({ ...p, responsibilities: e.target.value }))}
                        />
                      </div>
                      <div className="cmm-field">
                        <label>Requirements (one per line)</label>
                        <textarea
                          rows={4}
                          placeholder={"B.Tech / M.Sc degree\n2+ years teaching experience"}
                          value={positionForm.requirements}
                          onChange={e => setPositionForm(p => ({ ...p, requirements: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="cmm-form-actions">
                      {editingPosition && (
                        <button
                          type="button"
                          className="cmm-btn-ghost"
                          onClick={() => { setEditingPosition(null); setPositionForm({ title: '', description: '', location: '', responsibilities: '', requirements: '' }); }}
                        >Cancel</button>
                      )}
                      <button type="submit" className="cmm-btn-primary" disabled={savingPosition}>
                        {savingPosition ? 'Saving...' : editingPosition ? 'Update Position' : 'Add Position'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Existing Positions List */}
                <div className="cmm-list-section">
                  <h4>📌 Existing Positions ({positions.length})</h4>
                  {positions.length === 0 ? (
                    <p className="cmm-empty">No positions yet. Add one above.</p>
                  ) : (
                    <ul className="cmm-position-list">
                      {positions.map((pos) => (
                        <li key={pos.id} className={`cmm-position-item ${editingPosition?.id === pos.id ? 'editing' : ''}`}>
                          <div className="cmm-pos-info">
                            <span className="cmm-pos-title">{pos.title}</span>
                            {pos.location && <span className="cmm-pos-loc">📍 {pos.location}</span>}
                          </div>
                          <div className="cmm-pos-actions">
                            <button
                              className="cmm-btn-edit"
                              onClick={() => startEditPosition(pos)}
                            >✏️ Edit</button>
                            <button
                              className="cmm-btn-delete"
                              onClick={() => handleDeletePosition(pos.id, pos.title)}
                              disabled={deletingPositionId === pos.id}
                            >{deletingPositionId === pos.id ? '...' : '🗑️ Delete'}</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* ── Access Control Tab (superadmin only) ── */}
            {manageTab === 'access' && isSuperAdmin && (
              <div className="cmm-body">
                <div className="cmm-form-section">
                  <h4>➕ Grant Access to Admin</h4>
                  <p className="cmm-hint">Enter the email address of an admin user to allow them to manage career positions.</p>
                  <div className="cmm-grant-row">
                    <input
                      type="email"
                      placeholder="admin@example.com"
                      value={grantEmail}
                      onChange={e => setGrantEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleGrantAccess()}
                    />
                    <button
                      className="cmm-btn-primary"
                      onClick={handleGrantAccess}
                      disabled={granting}
                    >{granting ? 'Granting...' : 'Grant Access'}</button>
                  </div>
                </div>

                <div className="cmm-list-section">
                  <h4>👥 Authorized Users ({privilegedUsers.length})</h4>
                  {privilegedUsers.length === 0 ? (
                    <p className="cmm-empty">No admins granted access yet.</p>
                  ) : (
                    <ul className="cmm-access-list">
                      {privilegedUsers.map((pu) => (
                        <li key={pu.userId} className="cmm-access-item">
                          <div className="cmm-access-info">
                            <span className="cmm-access-name">{pu.userName || pu.userEmail}</span>
                            <span className="cmm-access-email">{pu.userEmail}</span>
                            {pu.grantedAt && (
                              <span className="cmm-access-date">
                                Granted: {new Date(pu.grantedAt.seconds * 1000).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <button
                            className="cmm-btn-delete"
                            onClick={() => handleRevokeAccess(pu.userId, pu.userName || pu.userEmail)}
                          >🚫 Revoke</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close-btn"
              onClick={() => setShowApplyModal(false)}
            >
              &times;
            </button>
            <h3>
              Apply for <br /> {formData.role}
            </h3>
            <form onSubmit={handleSubmit} className="application-form">
              <div className="form-group">
                <label>Full Name:</label>
                <input
                  type="text"
                  placeholder="Enter your full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email ID:</label>
                <input
                  type="email"
                  placeholder="Enter your Email ID"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mobile Number:</label>
                <input
                  type="tel"
                  placeholder="Enter your Mobile Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Updated Resume(PDF):</label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    name="resume"
                    onChange={handleChange}
                    accept=".pdf"
                    required
                  />
                  <div className="file-input-display">
                    <span className="file-input-text">
                      {formData.resume ? formData.resume.name : "No file chosen"}
                    </span>
                    <span className="file-input-button">Browse</span>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Login Required</h3>
            <p>You must login first to apply for this position.</p>
            <div className="login-modal-buttons">
              <button
                className="google-login-btn"
                onClick={async () => {
                  try {
                    const result = await signInWithPopup(auth, googleProvider);
                    if (result.user) {
                      setShowLoginModal(false);
                      toast.success(`Welcome, ${result.user.displayName}!`);
                    }
                  } catch (error) {
                    toast.error("Google login failed: " + error.message);
                  }
                }}
              >
                Login with Google
              </button>
              <button
                className="close-login-btn"
                onClick={() => setShowLoginModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      <ToastContainer position="top-right" autoClose={3000} />
      <Footer />
    </div>
  );
}
